import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { LanguageToggle } from "@/components/shared/LanguageToggle";
import { CtaButton } from "@/components/shared/CtaButton";
import { useLanguage } from "@/context/LanguageContext";
import { CONTENT } from "@/i18n/content";
import { cn } from "@/lib/utils";

export function Navbar() {
  const { lang } = useLanguage();
  const t = CONTENT[lang].nav;
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "sample", label: t.sample },
    { id: "how", label: t.how },
    { id: "powers", label: t.powers },
    { id: "pricing", label: t.pricing },
    { id: "faq", label: t.faq },
  ];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled ? "glass border-b border-navy/10 py-2.5 shadow-sm" : "bg-transparent py-4"
      )}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="ShubhSamay home" data-testid="nav-logo">
          <img
            src="/shubhsamay-logo.png"
            alt="ShubhSamay — Safalta Ka Sahi Samay"
            className={cn("w-auto object-contain transition-all duration-300", scrolled ? "h-9 sm:h-10" : "h-10 sm:h-12")}
          />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium text-navy/70 transition-colors hover:text-navy"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageToggle testIdPrefix="nav-lang" />
          <CtaButton variant="primary" testId="nav-cta" className="px-5 py-2.5 text-sm">
            {t.cta}
          </CtaButton>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageToggle compact testIdPrefix="topbar-lang" />
          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 bg-white/70 text-navy"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm border-navy/10 bg-cream">
              <div className="flex h-full flex-col">
                <img
                  src="/shubhsamay-logo.png"
                  alt="ShubhSamay — Safalta Ka Sahi Samay"
                  className="mb-8 mt-2 h-16 w-auto max-w-[260px] self-center object-contain"
                />
                <nav className="flex flex-col gap-1">
                  {links.map((l) => (
                    <SheetClose asChild key={l.id}>
                      <button
                        onClick={() => scrollTo(l.id)}
                        data-testid={`mobile-nav-link-${l.id}`}
                        className="rounded-xl px-4 py-3.5 text-left font-heading text-lg font-medium text-navy transition-colors hover:bg-navy/5"
                      >
                        {l.label}
                      </button>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-6">
                  <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-navy/50">Language</p>
                  <LanguageToggle testIdPrefix="mobile-lang" className="w-full justify-between" />
                </div>
                <div className="mt-auto pt-8">
                  <SheetClose asChild>
                    <CtaButton variant="primary" testId="mobile-cta" className="w-full">
                      {t.cta}
                    </CtaButton>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
